import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn } from '@angular/router';

import { map, catchError, of, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import {
    BootstrapService,
    StorageService,
    AuthFacadeService,
    ConfigService,
    ConfirmDialogService,
} from '@venecia/services';
import { GlobalConstants as GlobalConstants } from '@venecia/constants';
import { FlowToken } from '@venecia/apis/auth';
import { UrlBuilder } from '@venecia/builders';

export const authBootstrapGuard: CanActivateFn = (route, state) => {
    const bootstrap = inject(BootstrapService);
    const authFacade = inject(AuthFacadeService);
    const confirmDialogService = inject(ConfirmDialogService);
    const translateService = inject(TranslateService);
    const storageService = inject(StorageService);
    const configService = inject(ConfigService);

    if (bootstrap.isReady()) {
        return true;
    }

    const authParams = getAuthorizationParams(new Set(['code']), route);
    if (Object.keys(authParams).length > 0) {
        storageService.set<Record<string, string>>(GlobalConstants.AUTH_PARAMS, authParams);
    }

    const returnUrl = getReturnUrlOrDefault(storageService, route, configService);

    authFacade.flowCode.set(route.queryParams[GlobalConstants.FLOW_CODE]);

    return authFacade
        .bootstrap$()
        .pipe(
            map((accessToken: string) => {
                if (accessToken) {
                    bootstrap.markReady();
                    return true;
                }
                doRedirect(returnUrl, 'err.spa.reset-flow', translateService, confirmDialogService, storageService);
                return false;
            }),
            catchError((err) => {
                const errorCode = err?.body?.code || 'err.spa.sso';
                doRedirect(returnUrl, errorCode, translateService, confirmDialogService, storageService);
                return of(false);
            })
        );
};

async function doRedirect(returnUrl: string,
    errorKey: string,
    translateService: TranslateService,
    confirmDialogService: ConfirmDialogService,
    storageService: StorageService
): Promise<void> {
    storageService.remove(GlobalConstants.FLOW_BEARER_KEY);
    storageService.remove(GlobalConstants.FLOW_CODE);

    const errorMessage = await firstValueFrom(translateService.get(errorKey));
    await confirmDialogService.open({
        message: errorMessage,
        dialogMode: true
    });

    const urlBuilder = new UrlBuilder(returnUrl);
    window.location.href = urlBuilder.build('', {
        "error_code": errorKey,
        "theme": storageService.get<string>(GlobalConstants.THEME_KEY),
        "lang": storageService.get<string>(GlobalConstants.LANG_KEY)
    });
}

function getReturnUrlOrDefault(storageService: StorageService, route: ActivatedRouteSnapshot, configService: ConfigService): string {
    const flowToken: FlowToken | null = storageService.get<FlowToken>(GlobalConstants.FLOW_BEARER_KEY);

    return flowToken?.['redirect-to']
        || route.queryParams[GlobalConstants.APP_REDIRECT_URI]
        || configService.get("identity.url");
}

function getAuthorizationParams(excludedParams: Set<string>, routeSnapshot: ActivatedRouteSnapshot): Record<string, string> {
    const params: Record<string, string> = {};

    routeSnapshot.queryParamMap.keys
        .filter((key) => !excludedParams.has(key))
        .forEach((key) => {
            const value = routeSnapshot.queryParamMap.get(key);
            if (value !== null) {
                params[key] = value;
            }
        });

    return params;
}